// src/components/Authentication/SignUpForm/SignUpPage.jsx

import React, { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SignUpForm } from "./SignUpForm";
import { Box, Container, Link } from "@mui/material";
import { useStripeSession } from "../../../hooks/useStripe";
import FullScreenLoader from "../../Common/FullScreenLoader";

const subscriptionTiers = {
  basic: { name: "basic", price: "24.99" },
  pro: { name: "pro", price: "49.99" },
  default: { name: "Subscription", price: "0.00" },
};

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get params from URL
  const sessionId = searchParams.get("session_id");
  const urlEmail = searchParams.get("email");

  // Fetch session data (if sessionId exists)
  const { isLoading, email: sessionEmail, tier } = useStripeSession(sessionId);

  // Determine the email to pre-fill (Session takes priority, then URL)
  const defaultEmail = sessionEmail || urlEmail || "";

  // Redirect ONLY if we have finished loading AND we lack both a session AND an email
  useEffect(() => {
    if (!isLoading && !sessionId && !urlEmail) {
      window.location.href = "https://candidatetools.com/pricing";
    }
  }, [isLoading, sessionId, urlEmail]);

  // Only block render if we are actively loading data
  if (isLoading) {
    return <FullScreenLoader />;
  }

  // If we are not loading, and have neither session nor email, return null (redirecting)
  if (!sessionId && !urlEmail) return null;

  const subscription = subscriptionTiers[tier] || subscriptionTiers.default;

  const authStrings = {
    signUpHeader: "Create Account",
    signUpSubtitle: "Get started with Candidate Tools today.",
    subscribedHeader: `Subscribed to ${subscription.name}`,
    emailLabel: "Email Address",
    passwordLabel: "Password",
    confirmPasswordLabel: "Confirm Password",
    passwordsMustMatch: "Passwords must match.",
    switchToSignIn: "Already have an account? Sign in",
    submitButton: "Create Account",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "#f0f2f5",
      }}
    >
      <Container
        component="main"
        maxWidth="lg"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <SignUpForm
          strings={authStrings}
          defaultEmail={defaultEmail}
          sessionId={sessionId}
          subscription={subscription}
          emailDisabled={!!sessionId}
        />

        {/* Footer */}
        <Box sx={{ display: "flex", gap: 3, mt: 3, opacity: 0.7 }}>
          <Link
            href="/help"
            variant="body2"
            color="text.secondary"
            sx={{ textDecoration: "none", fontSize: "0.8rem" }}
          >
            Help
          </Link>
          <Link
            href="/privacy"
            variant="body2"
            color="text.secondary"
            sx={{ textDecoration: "none", fontSize: "0.8rem" }}
          >
            Privacy
          </Link>
          <Link
            href="/terms"
            variant="body2"
            color="text.secondary"
            sx={{ textDecoration: "none", fontSize: "0.8rem" }}
          >
            Terms
          </Link>
        </Box>
      </Container>
    </Box>
  );
}
